/**
 * Offline Member Cache Service
 *
 * Caches team members from the tenant members endpoint so field operators
 * can look up colleagues and see their avatars while offline.
 *
 * Read-only cache — members are not created/edited offline.
 */

import { db, CachedMember } from './db';
import { api } from '@/services/api';
import { photoCacheService } from './photo-cache.service';

const SYNC_META_KEY = 'member_cache_last_sync';

/**
 * Fetch team members from the server and cache them in IndexedDB.
 * Also downloads avatar photo blobs for offline rendering.
 */
async function cacheFromServer(tenantId: string): Promise<{ cached: number; photos: number }> {
  const result = { cached: 0, photos: 0 };

  try {
    const res = await api.get<Array<{
      user_id: string;
      email: string;
      name: string;
      role_name?: string;
      status: string;
      avatar_url?: string;
      phone?: string;
    }>>(`/tenants/${tenantId}/members`);

    const members: any[] = Array.isArray(res) ? res : (res as any).data ?? [];

    await db.transaction('rw', db.cachedMembers, async () => {
      // Replace entire cache for this tenant
      await db.cachedMembers.where('tenant_id').equals(tenantId).delete();

      const records: CachedMember[] = members.map((m) => ({
        id: m.user_id,
        tenant_id: tenantId,
        email: m.email ?? '',
        name: m.name ?? '',
        role_name: m.role_name,
        status: m.status ?? 'active',
        avatar_url: m.avatar_url,
        phone: m.phone,
        is_synced: true,
        synced_at: new Date().toISOString(),
      }));

      if (records.length > 0) {
        await db.cachedMembers.bulkPut(records);
      }
      result.cached = records.length;
    });

    // Cache avatar photo blobs
    const photoItems = members
      .filter((m) => m.avatar_url)
      .map((m) => ({ id: m.user_id, photoUrl: m.avatar_url }));
    result.photos = await photoCacheService.cachePhotos('member', photoItems);

    localStorage.setItem(
      `${SYNC_META_KEY}:${tenantId}`,
      new Date().toISOString()
    );
  } catch (error) {
    console.warn('[MemberCache] Failed to cache from server:', error);
  }

  return result;
}

/**
 * Query cached members with optional text search.
 */
async function getCached(tenantId: string, search?: string): Promise<CachedMember[]> {
  let results = await db.cachedMembers
    .where('tenant_id')
    .equals(tenantId)
    .toArray();

  if (search) {
    const lower = search.toLowerCase();
    results = results.filter(
      (m) =>
        m.name?.toLowerCase().includes(lower) ||
        m.email?.toLowerCase().includes(lower) ||
        m.role_name?.toLowerCase().includes(lower) ||
        m.phone?.toLowerCase().includes(lower)
    );
  }

  results.sort((a, b) => a.name.localeCompare(b.name));
  return results;
}

/**
 * Get a single cached member by user ID.
 */
async function getById(id: string): Promise<CachedMember | undefined> {
  return db.cachedMembers.get(id);
}

/**
 * Return the ISO timestamp of the last successful sync for this tenant.
 */
function getLastSyncTime(tenantId: string): string | null {
  return localStorage.getItem(`${SYNC_META_KEY}:${tenantId}`);
}

/**
 * Clear all cached members (e.g. on logout).
 */
async function clearCache(): Promise<void> {
  await db.cachedMembers.clear();
  await photoCacheService.clearCache('member');
}

export const memberCacheService = {
  cacheFromServer,
  getCached,
  getById,
  getLastSyncTime,
  clearCache,
};
